import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useIsSuperAdmin } from '@/hooks/useIsSuperAdmin';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface OrganizationOverride {
  id: string;
  company_name: string;
  industry: string;
}

interface CurrentOrganization {
  id: string;
  name?: string;
}

interface OrganizationContextType {
  activeOrganizationId: string | null;
  currentOrganization: CurrentOrganization | null;
  overriddenOrg: OrganizationOverride | null;
  isOverriding: boolean;
  setOrganizationOverride: (orgId: string, orgDetails: OrganizationOverride) => void;
  clearOverride: () => void;
  canOverride: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { isSuperAdmin } = useIsSuperAdmin();
  const [overriddenOrg, setOverriddenOrg] = useState<OrganizationOverride | null>(null);
  const [viewStartTime, setViewStartTime] = useState<string | null>(null);
  const [userOrganization, setUserOrganization] = useState<CurrentOrganization | null>(null);

  // Fetch user's organization from user_profiles
  useEffect(() => {
    let mounted = true;

    async function fetchUserOrg() {
      if (!user?.id) {
        setUserOrganization(null);
        return;
      }

      try {
        // Get user profile to find organization_profile_id
        const { data: userProfile, error: userError } = await supabase
          .from('user_profiles')
          .select('organization_profile_id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!mounted) return;

        if (userError) {
          console.error('Failed to fetch user profile:', userError);
          return;
        }

        if (userProfile?.organization_profile_id) {
          // Fetch the org details
          const { data: orgData, error: orgError } = await supabase
            .from('organization_profiles')
            .select('id, company_name')
            .eq('id', userProfile.organization_profile_id)
            .maybeSingle();

          if (!mounted) return;

          if (orgError) {
            console.error('Failed to fetch org profile:', orgError);
            return;
          }

          if (orgData) {
            setUserOrganization({
              id: orgData.id,
              name: orgData.company_name
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch user organization:', error);
      }
    }

    fetchUserOrg();
    return () => { mounted = false; };
  }, [user?.id]);

  // Log view when override starts
  const logViewStart = async (orgId: string) => {
    if (!user?.id) return;
    
    try {
      const startTime = new Date().toISOString();
      setViewStartTime(startTime);
      
      await supabase.from('super_admin_org_views').insert({
        super_admin_id: user.id,
        viewed_organization_id: orgId,
        started_at: startTime,
      });
    } catch (error) {
      console.error('Failed to log org view start:', error);
    }
  };

  // Log view end when override clears
  const logViewEnd = async (orgId: string, startTime: string) => {
    if (!user?.id) return;
    
    try {
      await supabase
        .from('super_admin_org_views')
        .update({ ended_at: new Date().toISOString() })
        .eq('super_admin_id', user.id)
        .eq('viewed_organization_id', orgId)
        .eq('started_at', startTime);
    } catch (error) {
      console.error('Failed to log org view end:', error);
    }
  };

  const setOrganizationOverride = (orgId: string, orgDetails: OrganizationOverride) => {
    if (!isSuperAdmin) return;
    
    setOverriddenOrg(orgDetails);
    logViewStart(orgId);
  };

  const clearOverride = () => {
    if (overriddenOrg && viewStartTime) {
      logViewEnd(overriddenOrg.id, viewStartTime);
    }
    setOverriddenOrg(null);
    setViewStartTime(null);
  };

  // Clear override on logout
  useEffect(() => {
    if (!user) {
      setOverriddenOrg(null);
      setViewStartTime(null);
    }
  }, [user]);

  // Compute the active organization - override takes precedence
  const activeOrgId = overriddenOrg?.id || userOrganization?.id || null;
  const currentOrg: CurrentOrganization | null = overriddenOrg 
    ? { id: overriddenOrg.id, name: overriddenOrg.company_name }
    : userOrganization;

  const value: OrganizationContextType = {
    activeOrganizationId: activeOrgId,
    currentOrganization: currentOrg,
    overriddenOrg,
    isOverriding: !!overriddenOrg,
    setOrganizationOverride,
    clearOverride,
    canOverride: isSuperAdmin,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganizationContext() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganizationContext must be used within an OrganizationProvider');
  }
  return context;
}

// Alias for convenience - matches expected import name
export const useOrganization = useOrganizationContext;
